import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { DependencyFilterKeys } from '../../../../shared/dependenciesV2';
import { ENTERPRISE_PLATFORM } from '../../../../shared/subscriptions/consts';
import { isBusinessTier, isEnterpriseTier } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import { Icon, icons, SearchInput } from '../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import Flex from '../../generic/Flex';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';

import useFetchDependencyAction from './hooks/useFetchDependencyAction';
import type { DependencyV2Modals } from './hooks/useDependencyModals';

import styles from './dependencies.module.less';

type Props = {
  handleAddManualDependency: () => void;
  depModals: DependencyV2Modals;
};

const DependenciesHeader: VoidFunctionComponent<Props> = ({
  handleAddManualDependency,
  depModals: { managePackageLabelsModal },
}) => {
  const setSearchFilterAndFetch = useFetchDependencyAction(DependenciesV2Store.actions.setFilters);
  const filters = useSelector(DependenciesV2Store.selectors.getFilters);
  const isProjectScope = useSelector(DependenciesV2Store.selectors.getIsProjectScope);
  const isOrgMember = useSelector(DependenciesV2Store.selectors.getIsOrgMember);

  const plan = window.user?.organization?.stripe_plan;
  const handleSearchInput = (value: string) =>
    setSearchFilterAndFetch({
      ...filters,
      [DependencyFilterKeys.Title]: value,
    });

  const onClickAssignLabels = () => {
    // Open modal without revision to trigger bulk mode
    managePackageLabelsModal.onOpen({ context: {} });
  };

  return (
    <div className={styles.DependenciesHeader}>
      <div className={styles.DependenciesHeaderSearch}>
        <SearchInput
          label="dependency-name"
          placeholder="Filter by name or locator"
          size="medium"
          value={filters.title}
          onChange={handleSearchInput}
        />
      </div>
      <Flex direction="row" gap="8px">
        {isProjectScope && (
          <PaidFeatureGate
            isRestricted={!(isEnterpriseTier({ plan }) || isBusinessTier({ plan }))}
            planToShow={ENTERPRISE_PLATFORM}
            hidePill
          >
            <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.LARGE} onClick={onClickAssignLabels}>
              <div>
                <Icon icon={icons.sliders} />
                Assign Labels
              </div>
            </Button>
          </PaidFeatureGate>
        )}
        {isOrgMember && isProjectScope && (
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.LARGE} onClick={handleAddManualDependency}>
            Add Dependency
          </Button>
        )}
      </Flex>
    </div>
  );
};

export default DependenciesHeader;
